import { useState, useEffect, useCallback } from 'react';
import {
  Download,
  Calendar,
  FileText,
  Plus,
  RefreshCw,
  TrendingUp,
  Activity,
} from 'lucide-react';
import DataTable from '../../components/common/DataTable';
import type { Column } from '../../components/common/DataTable';
import Modal from '../../components/common/Modal';
import ReactApexChart from 'react-apexcharts';
import type { ApexOptions } from 'apexcharts';
import { observabilityService } from '../../services/observability.service';
import applicationService from '../../services/application.service';
import { useToast } from '../../components/common/ToastContainer';
import { useTenantStore } from '../../stores/tenantStore';
import { formatTimeOnly } from '../../utils/formatDate';
import { useSettingsStore } from '../../stores/settingsStore';
import { usePermissions } from '../../hooks/usePermissions';

type ReportView = 'daily' | 'weekly' | 'monthly';

// Maximum bytes/sec per interface (10 Gbps) — filters out counter-wrap corruption
// from agent uint64 underflow when VPP counters reset. Actual edge NICs are 1–2.5 Gbps.
const MAX_IFACE_BYTES_PER_SEC = 1_250_000_000;

interface Edge {
  id: string;
  name: string;
}

interface EdgeUsageReport {
  edgeId: string;
  edgeName: string;
  totalTrafficGB: number;
  avgDailyTrafficGB: number;
  peakBandwidthMbps: number;
  avgCpu: number;
  avgMemory: number;
  hasTraffic: boolean;
  hasStats: boolean;
}

interface ApplicationReport {
  appName: string;
  appId: string;
  totalTrafficGB: number;
  percentage: number;
  bandwidthMbps: number;
}

// Well-known port fallback map
const PORT_NAMES: Record<string, string> = {
  '22': 'SSH', '53': 'DNS', '80': 'HTTP', '443': 'HTTPS', '853': 'DNS-over-TLS',
  '1883': 'MQTT', '3306': 'MySQL', '5432': 'PostgreSQL', '6379': 'Redis',
  '8080': 'HTTP-Alt', '8443': 'HTTPS-Alt',
};

const UsageReports = () => {
  const toast = useToast();
  const { canCreate } = usePermissions();
  const { getDateFormatSettings } = useSettingsStore();
  const dateSettings = getDateFormatSettings();
  const { getTenantId } = useTenantStore();
  // Subscribe to selectedTenantId as a value so fetchData rebuilds on
  // tenant switch — see VSNP-670 audit. Without this, getTenantId is a
  // stable Zustand function reference, the useCallback identity never
  // changes, and the report keeps showing the previous tenant's data.
  const selectedTenantId = useTenantStore((s) => s.selectedTenantId);
  const [reportView, setReportView] = useState<ReportView>('weekly');
  const [loading, setLoading] = useState(true);
  const [lastRefreshed, setLastRefreshed] = useState(new Date());
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);

  // Filter states
  const [hideNoTraffic, setHideNoTraffic] = useState(false);
  const [hideNoStats, setHideNoStats] = useState(false);

  // Data states
  const [edges, setEdges] = useState<Edge[]>([]);
  const [edgeUsageReports, setEdgeUsageReports] = useState<EdgeUsageReport[]>([]);
  const [applicationReports, setApplicationReports] = useState<ApplicationReport[]>([]);

  // Stats
  const [totalNetworkTrafficGB, setTotalNetworkTrafficGB] = useState(0);
  const [avgDailyNetworkGB, setAvgDailyNetworkGB] = useState(0);

  // Get time range based on report view
  const getTimeRangeSeconds = (view: ReportView): number => {
    switch (view) {
      case 'daily': return 86400;
      case 'weekly': return 7 * 86400;
      case 'monthly': return 30 * 86400;
    }
  };

  const getDays = (view: ReportView): number => {
    switch (view) {
      case 'daily': return 1;
      case 'weekly': return 7;
      case 'monthly': return 30;
    }
  };

  // Fetch all data
  const fetchData = useCallback(async () => {
    setLoading(true);

    try {
      const end = Math.floor(Date.now() / 1000);
      const rangeSeconds = getTimeRangeSeconds(reportView);
      const start = end - rangeSeconds;
      const step = Math.floor(rangeSeconds / 100);
      const days = getDays(reportView);

      // Fetch edges
      const edgesRes = await observabilityService.getRegisteredEdges();
      const edgeList = edgesRes.status === 'success' ? edgesRes.data || [] : [];
      setEdges(edgeList);

      if (edgeList.length === 0) {
        setLoading(false);
        return;
      }

      // Fetch per-edge usage data
      // clamp_max filters counter-wrap corruption from agent uint64 underflow on VPP restart
      const maxIncrease = MAX_IFACE_BYTES_PER_SEC * rangeSeconds;
      const reports: EdgeUsageReport[] = [];
      let networkTotalBytes = 0;

      for (const edge of edgeList) {
        try {
          const [rxRes, txRes, rxRateRes, txRateRes, cpuRes, memRes] = await Promise.all([
            observabilityService.query(
              `sum(clamp_max(increase(edge_interface_rx_bytes_total{edge_id="${edge.id}", role=~"wan|lan"}[${rangeSeconds}s]), ${maxIncrease}))`
            ),
            observabilityService.query(
              `sum(clamp_max(increase(edge_interface_tx_bytes_total{edge_id="${edge.id}", role=~"wan|lan"}[${rangeSeconds}s]), ${maxIncrease}))`
            ),
            observabilityService.query(
              `sum(clamp_max(rate(edge_interface_rx_bytes_total{edge_id="${edge.id}", role=~"wan|lan"}[${rangeSeconds}s]), ${MAX_IFACE_BYTES_PER_SEC}))`
            ),
            observabilityService.query(
              `sum(clamp_max(rate(edge_interface_tx_bytes_total{edge_id="${edge.id}", role=~"wan|lan"}[${rangeSeconds}s]), ${MAX_IFACE_BYTES_PER_SEC}))`
            ),
            observabilityService.queryRange(
              `edge_cpu_usage_percent{edge_id="${edge.id}"}`,
              start, end, step
            ),
            observabilityService.queryRange(
              `edge_memory_bytes{edge_id="${edge.id}"} / edge_memory_total_bytes{edge_id="${edge.id}"} * 100`,
              start, end, step
            ),
          ]);

          // Calculate traffic
          let rxTotal = 0;
          let txTotal = 0;
          let peakMbps = 0;

          if (rxRes.status === 'success' && rxRes.data?.result?.length > 0) {
            rxTotal = parseFloat(rxRes.data.result[0].value?.[1] || '0');
          }

          if (txRes.status === 'success' && txRes.data?.result?.length > 0) {
            txTotal = parseFloat(txRes.data.result[0].value?.[1] || '0');
          }

          // Use rate for peak bandwidth (converted to Mbps)
          if (rxRateRes.status === 'success' && rxRateRes.data?.result?.length > 0) {
            const rxRate = parseFloat(rxRateRes.data.result[0].value?.[1] || '0');
            const txRate = (txRateRes.status === 'success' && txRateRes.data?.result?.length > 0)
              ? parseFloat(txRateRes.data.result[0].value?.[1] || '0')
              : 0;
            peakMbps = ((rxRate + txRate) * 8) / 1000000;
          }

          // Calculate CPU avg
          let avgCpu = 0;
          if (cpuRes.status === 'success' && cpuRes.data?.result?.length > 0) {
            const values = cpuRes.data.result[0].values || [];
            if (values.length > 0) {
              const sum = values.reduce((acc: number, [, val]: [number, string]) => acc + parseFloat(val), 0);
              avgCpu = sum / values.length;
            }
          }

          // Calculate Memory avg
          let avgMemory = 0;
          if (memRes.status === 'success' && memRes.data?.result?.length > 0) {
            const values = memRes.data.result[0].values || [];
            if (values.length > 0) {
              const sum = values.reduce((acc: number, [, val]: [number, string]) => acc + parseFloat(val), 0);
              avgMemory = sum / values.length;
            }
          }

          const totalBytes = Math.max(0, rxTotal) + Math.max(0, txTotal);
          networkTotalBytes += totalBytes;

          const hasTraffic = totalBytes > 0;
          const hasStats = avgCpu > 0 || avgMemory > 0;

          reports.push({
            edgeId: edge.id,
            edgeName: edge.name,
            totalTrafficGB: Math.round((totalBytes / (1024 * 1024 * 1024)) * 100) / 100,
            avgDailyTrafficGB: Math.round((totalBytes / (1024 * 1024 * 1024) / days) * 100) / 100,
            peakBandwidthMbps: Math.round(peakMbps * 10) / 10,
            avgCpu: Math.round(avgCpu * 10) / 10,
            avgMemory: Math.round(avgMemory * 10) / 10,
            hasTraffic,
            hasStats,
          });
        } catch {
          // Skip edge on error
        }
      }

      setEdgeUsageReports(reports.sort((a, b) => b.totalTrafficGB - a.totalTrafficGB));
      setTotalNetworkTrafficGB(Math.round((networkTotalBytes / (1024 * 1024 * 1024)) * 100) / 100);
      setAvgDailyNetworkGB(Math.round((networkTotalBytes / (1024 * 1024 * 1024) / days) * 100) / 100);

      // Fetch application reports from IPFIX port data + application definitions
      try {
        // Get top ports by bytes from IPFIX
        const portQuery = `topk(15, sum by (dst_port) (increase(edge_ipfix_bytes_total[${rangeSeconds}s])))`;
        const [portRes, appsListRes] = await Promise.all([
          observabilityService.query(portQuery),
          applicationService.list({ pageSize: 100, ...(getTenantId() ? { tenantId: getTenantId() } : {}) }),
        ]);

        // Build port→app name map from application definitions
        const portToApp: Record<string, string> = {};
        for (const app of (appsListRes?.items || []).filter(a => a.enabled === 1)) {
          if (!app.destination_ports || app.destination_ports === 'any') continue;
          for (const seg of app.destination_ports.split(',')) {
            const s = seg.trim();
            const rangeParts = s.split('-');
            if (rangeParts.length === 2) {
              const lo = parseInt(rangeParts[0], 10);
              const hi = parseInt(rangeParts[1], 10);
              if (!isNaN(lo) && !isNaN(hi) && hi - lo < 100) {
                for (let p = lo; p <= hi; p++) {
                  if (!portToApp[String(p)]) portToApp[String(p)] = app.name;
                }
              }
            } else if (!portToApp[s]) {
              portToApp[s] = app.name;
            }
          }
        }

        // Build application reports from port traffic
        const appReports: ApplicationReport[] = [];
        let totalBytes = 0;
        if (portRes?.data?.result) {
          for (const result of portRes.data.result) {
            const port = result.metric?.dst_port;
            const bytes = parseFloat(result.value?.[1] || '0');
            if (!port || bytes <= 0) continue;
            totalBytes += bytes;
            const appName = portToApp[port] || PORT_NAMES[port] || `Port ${port}`;
            const existing = appReports.find(r => r.appName === appName);
            if (existing) {
              existing.bandwidthMbps += (bytes * 8) / (rangeSeconds * 1000000);
              existing.totalTrafficGB += bytes / (1024 * 1024 * 1024);
            } else {
              appReports.push({
                appName,
                appId: port,
                totalTrafficGB: bytes / (1024 * 1024 * 1024),
                percentage: 0,
                bandwidthMbps: (bytes * 8) / (rangeSeconds * 1000000),
              });
            }
          }
          // Calculate percentages
          for (const r of appReports) {
            r.percentage = totalBytes > 0 ? ((r.totalTrafficGB * 1024 * 1024 * 1024) / totalBytes) * 100 : 0;
          }
        }
        setApplicationReports(appReports.sort((a, b) => b.bandwidthMbps - a.bandwidthMbps));
      } catch {
        setApplicationReports([]);
      }

      setLastRefreshed(new Date());
    } catch (error) {
      console.error('Failed to fetch usage reports:', error);
    } finally {
      setLoading(false);
    }
  }, [reportView, selectedTenantId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Edge Usage Report Columns
  const edgeReportColumns: Column<EdgeUsageReport>[] = [
    {
      key: 'edgeName',
      label: 'Edge Device',
      sortable: true,
      render: (report) => (
        <div>
          <div className="font-medium text-gray-900">{report.edgeName}</div>
          <div className="text-xs text-gray-500 font-mono">{report.edgeId.slice(0, 8)}...</div>
        </div>
      ),
    },
    {
      key: 'totalTrafficGB',
      label: 'Total Traffic',
      sortable: true,
      render: (report) => (
        <span className="font-semibold text-primary-600">{report.totalTrafficGB} GB</span>
      ),
    },
    {
      key: 'avgDailyTrafficGB',
      label: 'Avg Daily',
      sortable: true,
      render: (report) => <span className="text-sm text-gray-900">{report.avgDailyTrafficGB} GB</span>,
    },
    {
      key: 'peakBandwidthMbps',
      label: 'Peak Bandwidth',
      sortable: true,
      render: (report) => (
        <span className="text-sm text-gray-900">{report.peakBandwidthMbps} Mbps</span>
      ),
    },
    {
      key: 'avgCpu',
      label: 'Avg CPU',
      sortable: true,
      render: (report) => (
        <span className={`text-sm ${report.avgCpu > 80 ? 'text-danger-600' : report.avgCpu > 60 ? 'text-warning-600' : 'text-gray-900'}`}>
          {report.avgCpu}%
        </span>
      ),
    },
    {
      key: 'avgMemory',
      label: 'Avg Memory',
      sortable: true,
      render: (report) => (
        <span className={`text-sm ${report.avgMemory > 80 ? 'text-danger-600' : report.avgMemory > 60 ? 'text-warning-600' : 'text-gray-900'}`}>
          {report.avgMemory}%
        </span>
      ),
    },
  ];

  // Edge Traffic Chart
  const edgeChartOptions: ApexOptions = {
    chart: {
      type: 'bar',
      toolbar: { show: false },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '55%',
        borderRadius: 4,
      },
    },
    dataLabels: { enabled: false },
    colors: ['#3b82f6', '#10b981'],
    xaxis: {
      categories: edgeUsageReports.slice(0, 10).map(e => e.edgeName.slice(0, 12)),
      labels: {
        style: { colors: '#64748b', fontSize: '11px' },
        rotate: -45,
      },
    },
    yaxis: {
      title: { text: 'Traffic (GB)', style: { color: '#64748b' } },
      labels: {
        style: { colors: '#64748b', fontSize: '11px' },
      },
    },
    legend: {
      position: 'top',
      fontSize: '12px',
    },
    grid: {
      borderColor: '#f1f5f9',
      strokeDashArray: 4,
    },
  };

  const edgeChartSeries = [
    {
      name: 'Total Traffic',
      data: edgeUsageReports.slice(0, 10).map(e => e.totalTrafficGB),
    },
    {
      name: 'Avg Daily',
      data: edgeUsageReports.slice(0, 10).map(e => e.avgDailyTrafficGB),
    },
  ];

  // Application Chart
  const applicationChartOptions: ApexOptions = {
    chart: {
      type: 'bar',
      toolbar: { show: false },
    },
    plotOptions: {
      bar: {
        horizontal: true,
        borderRadius: 4,
      },
    },
    dataLabels: { enabled: false },
    colors: ['#8b5cf6'],
    xaxis: {
      title: { text: 'Bandwidth (Mbps)', style: { color: '#64748b' } },
      labels: {
        style: { colors: '#64748b', fontSize: '11px' },
      },
    },
    yaxis: {
      labels: {
        style: { colors: '#64748b', fontSize: '11px' },
      },
    },
    grid: {
      borderColor: '#f1f5f9',
      strokeDashArray: 4,
    },
  };

  const applicationChartSeries = [
    {
      name: 'Traffic (MB)',
      data: applicationReports.slice(0, 10).map(app => ({
        x: app.appName.slice(0, 20),
        y: Math.round(app.totalTrafficGB * 1024 * 100) / 100, // GB → MB
      })),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Usage Reports</h1>
          <p className="text-gray-600 mt-1">Bandwidth usage reports across your network</p>
        </div>
        <div className="flex items-center space-x-3">
          <span className="text-sm text-gray-500">
            Updated: {formatTimeOnly(lastRefreshed, dateSettings)}
          </span>
          <button
            onClick={fetchData}
            disabled={loading}
            className="btn-secondary inline-flex items-center space-x-2"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Mock Data Notice - Only for scheduled reports */}
      <div className="bg-amber-50 border-l-4 border-amber-400 p-4">
        <div className="flex">
          <div className="ml-3">
            <p className="text-sm text-amber-700 font-medium">
              <strong>Note:</strong> Scheduled report generation (email delivery, PDF export) is not yet implemented.
              All traffic and usage data shown is real-time from your edge devices.
            </p>
          </div>
        </div>
      </div>

      {/* Report Type Selection */}
      <div className="card">
        <div className="flex flex-wrap items-center justify-between gap-4">
          {/* View Selector */}
          <div className="flex items-center bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setReportView('daily')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${reportView === 'daily'
                ? 'bg-white text-primary-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
                }`}
            >
              Daily
            </button>
            <button
              onClick={() => setReportView('weekly')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${reportView === 'weekly'
                ? 'bg-white text-primary-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
                }`}
            >
              Weekly
            </button>
            <button
              onClick={() => setReportView('monthly')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${reportView === 'monthly'
                ? 'bg-white text-primary-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
                }`}
            >
              Monthly
            </button>
          </div>

          {/* Summary Stats */}
          <div className="flex items-center space-x-6">
            <div className="flex items-center space-x-2">
              <TrendingUp className="w-5 h-5 text-primary-600" />
              <div>
                <p className="text-xs text-gray-500">Total Traffic</p>
                <p className="text-lg font-bold text-gray-900">{totalNetworkTrafficGB} GB</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Activity className="w-5 h-5 text-success-600" />
              <div>
                <p className="text-xs text-gray-500">Avg Daily</p>
                <p className="text-lg font-bold text-gray-900">{avgDailyNetworkGB} GB</p>
              </div>
            </div>
          </div>

          {/* Export Button */}
          <button className="btn-primary inline-flex items-center space-x-2">
            <Download className="w-4 h-4" />
            <span>Export CSV</span>
          </button>
        </div>
      </div>

      {/* Edge Usage Reports */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Edge Device Usage</h3>
          <div className="flex items-center space-x-4">
            <label className="flex items-center space-x-2 text-sm text-gray-600">
              <input
                type="checkbox"
                checked={hideNoTraffic}
                onChange={(e) => setHideNoTraffic(e.target.checked)}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span>Hide no traffic</span>
            </label>
            <label className="flex items-center space-x-2 text-sm text-gray-600">
              <input
                type="checkbox"
                checked={hideNoStats}
                onChange={(e) => setHideNoStats(e.target.checked)}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span>Hide offline/no stats</span>
            </label>
            <span className="text-sm text-gray-500">{edges.length} devices</span>
          </div>
        </div>
        <DataTable
          columns={edgeReportColumns}
          data={edgeUsageReports.filter(e => {
            if (hideNoTraffic && !e.hasTraffic) return false;
            if (hideNoStats && !e.hasStats) return false;
            return true;
          })}
          loading={loading}
          searchable
          searchPlaceholder="Search edges..."
        />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Edge Traffic Chart */}
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Usage by Edge Device</h3>
          {loading ? (
            <div className="h-80 flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
            </div>
          ) : edgeUsageReports.length === 0 ? (
            <div className="h-80 flex items-center justify-center text-gray-500">
              <p>No usage data available</p>
            </div>
          ) : (
            <ReactApexChart
              options={edgeChartOptions}
              series={edgeChartSeries}
              type="bar"
              height={300}
            />
          )}
        </div>

        {/* Application Chart */}
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Usage by Application</h3>
          {loading ? (
            <div className="h-80 flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
            </div>
          ) : applicationReports.length === 0 ? (
            <div className="h-80 flex flex-col items-center justify-center text-gray-500">
              <FileText className="w-12 h-12 text-gray-300 mb-4" />
              <p className="text-sm">No application traffic data available</p>
              <p className="text-xs mt-1">Enable IPFIX flow export on edges to see application usage</p>
            </div>
          ) : (
            <ReactApexChart
              options={applicationChartOptions}
              series={applicationChartSeries}
              type="bar"
              height={300}
            />
          )}
        </div>
      </div>

      {/* Application Usage Table */}
      {applicationReports.length > 0 && (
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Application Traffic Details</h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Application
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Traffic
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Share
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {applicationReports.map((app) => (
                  <tr key={app.appId} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">
                      {app.appName}
                    </td>
                    <td className="px-4 py-3 text-sm font-semibold text-primary-600">
                      {app.totalTrafficGB >= 1
                        ? `${app.totalTrafficGB.toFixed(2)} GB`
                        : app.totalTrafficGB * 1024 >= 1
                          ? `${(app.totalTrafficGB * 1024).toFixed(1)} MB`
                          : `${(app.totalTrafficGB * 1024 * 1024).toFixed(0)} KB`}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center space-x-2">
                        <div className="flex-1 h-2 bg-gray-200 rounded-full max-w-[100px]">
                          <div
                            className="h-2 bg-purple-500 rounded-full"
                            style={{ width: `${Math.min(app.percentage, 100)}%` }}
                          />
                        </div>
                        <span className="text-sm text-gray-600">{app.percentage.toFixed(1)}%</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Scheduled Reports Section */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Scheduled Reports</h3>
          {canCreate && (
            <button
              onClick={() => setIsScheduleModalOpen(true)}
              className="btn-primary inline-flex items-center space-x-2"
            >
              <Plus className="w-4 h-4" />
              <span>Schedule Report</span>
            </button>
          )}
        </div>
        <div className="text-center py-8 text-gray-500">
          <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-2" />
          <p className="text-sm">Scheduled report generation coming soon</p>
          <p className="text-xs mt-1">Automatic email delivery and PDF export</p>
        </div>
      </div>

      {/* Schedule Report Modal */}
      <Modal
        isOpen={isScheduleModalOpen}
        onClose={() => setIsScheduleModalOpen(false)}
        title="Schedule New Report"
        footer={
          <div className="flex justify-end space-x-3">
            <button
              onClick={() => setIsScheduleModalOpen(false)}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                toast.info('Coming Soon', 'Scheduled reports will be available in a future update.');
                setIsScheduleModalOpen(false);
              }}
              className="btn-primary inline-flex items-center space-x-2"
            >
              <Calendar className="w-4 h-4" />
              <span>Schedule</span>
            </button>
          </div>
        }
      >
        <div className="space-y-4">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
            <p className="text-sm text-amber-700">
              Scheduled report generation is not yet implemented. This feature will allow automatic
              email delivery of reports on a daily, weekly, or monthly basis.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Report Name <span className="text-red-500">*</span>
            </label>
            <input type="text" className="input-base w-full" placeholder="e.g., Weekly Bandwidth Summary" />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Report Type <span className="text-red-500">*</span>
            </label>
            <select className="input-base w-full">
              <option>Bandwidth Usage</option>
              <option>Edge Device Summary</option>
              <option>Application Traffic</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Frequency <span className="text-red-500">*</span>
            </label>
            <select className="input-base w-full">
              <option>Daily</option>
              <option>Weekly</option>
              <option>Monthly</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Recipients <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              className="input-base w-full"
              placeholder="email1@example.com, email2@example.com"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Format <span className="text-red-500">*</span>
            </label>
            <select className="input-base w-full">
              <option>PDF</option>
              <option>CSV</option>
              <option>Excel</option>
            </select>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default UsageReports;
